// MobileRoster — premium mobile day-view for Managers / Team Leaders.
// Design inspiration: Apple Calendar + Linear. Big touch targets, tabular numbers,
// glassmorphism header, floating FAB with safe-area, swipeable week strip.
import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ChevronLeft, ChevronRight, Coffee, AlertCircle, Users, ListChecks, Send, Copy, MoreHorizontal } from "lucide-react";
import useTodayISO from "@/lib/useTodayISO";
import PaperPlaneEmpty from "@/components/PaperPlaneEmpty";

const WEEKDAYS_NL = ["ma", "di", "wo", "do", "vr", "za", "zo"];

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shiftHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.round((mins / 60) * 10) / 10;
}
function fmtHours(h) {
  const n = Number(h) || 0;
  return Number.isInteger(n) ? `${n}u` : `${n.toFixed(1)}u`;
}

function EmployeeAvatar({ name, size = 32 }) {
  const initials = (name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span
      className="grid place-items-center rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(217_91%_45%)] text-white font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initials}
    </span>
  );
}

/**
 * Compact vertical shift card, optimised for phones.
 * Left rail = location colour. Big time. Prominent employee. Meta chips at bottom.
 */
function ShiftCard({ shift, employeeName, locName, locColor, onClick, index }) {
  const dur = shiftHours(shift.start_time, shift.end_time);
  const isUnassigned = !shift.employee_id;
  const isDraft = shift.status === "draft";

  return (
    <motion.button
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.025, duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
      onClick={onClick}
      data-testid={`mshift-${shift._id}`}
      className="w-full text-left rounded-2xl bg-[hsl(var(--surface))] border hairline shadow-[var(--shadow-xs)] active:scale-[0.985] transition-transform overflow-hidden"
    >
      <div className="flex items-stretch">
        {/* Location colour rail */}
        <div className="w-1.5 shrink-0" style={{ background: locColor }} />
        <div className="flex-1 min-w-0 p-3.5">
          {/* Row 1: time + duration */}
          <div className="flex items-baseline justify-between gap-2">
            <div className="font-display text-[19px] font-bold tabular-nums leading-none text-[hsl(var(--text-strong))]">
              {shift.start_time}
              <span className="text-[hsl(var(--text-muted))] font-normal mx-1.5">→</span>
              {shift.end_time}
            </div>
            <span className="text-[11px] font-bold tabular-nums text-[hsl(var(--primary))] shrink-0">
              {fmtHours(dur)}
            </span>
          </div>

          {/* Row 2: employee */}
          <div className="flex items-center gap-2.5 mt-3">
            {isUnassigned ? (
              <>
                <span className="grid place-items-center w-8 h-8 rounded-full bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] shrink-0">
                  <AlertCircle size={16} strokeWidth={2} />
                </span>
                <span className="text-[14px] font-semibold text-[hsl(var(--danger))]">
                  {employeeName /* which is "Onbezet" here */}
                </span>
              </>
            ) : (
              <>
                <EmployeeAvatar name={employeeName} size={32} />
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-[hsl(var(--text-strong))] truncate">
                    {employeeName}
                  </div>
                  {shift.role_label && (
                    <div className="text-[11px] text-[hsl(var(--text-muted))] truncate mt-0.5">
                      {shift.role_label}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Row 3: meta chips */}
          {(locName || (typeof shift.pause_minutes === "number" && shift.pause_minutes > 0) || isDraft) && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
              {locName && (
                <span
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10.5px] font-semibold"
                  style={{ background: `${locColor}15`, color: locColor }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: locColor }} />
                  {locName}
                </span>
              )}
              {typeof shift.pause_minutes === "number" && shift.pause_minutes > 0 && (
                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-[hsl(var(--muted))] text-[10.5px] font-semibold text-[hsl(var(--text-muted))] tabular-nums">
                  <Coffee size={10} strokeWidth={2.25} />
                  {shift.pause_minutes}m
                </span>
              )}
              {isDraft && (
                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-[hsl(var(--warning-soft,45_100%_92%))] text-[10.5px] font-semibold text-[hsl(var(--warning))]">
                  ●
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.button>
  );
}

/**
 * Employee row in "Team" tab: shows the employee + all their shifts on the selected day.
 */
function EmployeeRow({ employee, shifts, locColor, locName, onEditShift, onAddShift, dayISO, t, index }) {
  const totalHours = shifts.reduce((sum, s) => sum + shiftHours(s.start_time, s.end_time), 0);
  const off = shifts.length === 0;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.02, duration: 0.24 }}
      className="rounded-2xl bg-[hsl(var(--surface))] border hairline p-3.5 shadow-[var(--shadow-xs)]"
      data-testid={`mteam-row-${employee._id}`}
    >
      <div className="flex items-center gap-3">
        <EmployeeAvatar name={employee.name} size={40} />
        <div className="min-w-0 flex-1">
          <div className="text-[14.5px] font-semibold text-[hsl(var(--text-strong))] truncate">
            {employee.name}
          </div>
          <div className="text-[11.5px] text-[hsl(var(--text-muted))] mt-0.5 tabular-nums">
            {off ? t("mroster_off_today") : `${shifts.length} · ${fmtHours(totalHours)}`}
          </div>
        </div>
        <button
          onClick={() => onAddShift(dayISO, employee._id)}
          data-testid={`mteam-add-${employee._id}`}
          className="w-9 h-9 grid place-items-center rounded-full bg-[hsl(var(--muted))] active:scale-95 transition-transform"
          aria-label={t("add_shift")}
        >
          <Plus size={16} strokeWidth={2.25} />
        </button>
      </div>

      {shifts.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {shifts.map((s) => {
            const dur = shiftHours(s.start_time, s.end_time);
            return (
              <button
                key={s._id}
                onClick={() => onEditShift(s)}
                data-testid={`mteam-shift-${s._id}`}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl bg-[hsl(var(--muted))] active:scale-[0.985] transition-transform text-left"
                style={{ borderLeft: `3px solid ${locColor(s.location_id)}` }}
              >
                <span className="font-mono text-[13px] font-bold tabular-nums text-[hsl(var(--text-strong))] shrink-0">
                  {s.start_time}–{s.end_time}
                </span>
                <span className="text-[11.5px] text-[hsl(var(--text-muted))] tabular-nums shrink-0">
                  {fmtHours(dur)}
                </span>
                {locName(s.location_id) && (
                  <span className="text-[11px] font-medium truncate ml-auto" style={{ color: locColor(s.location_id) }}>
                    {locName(s.location_id)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

/**
 * Main mobile roster component.
 *   Props:
 *   - days: Date[]           (the 7 days of the current week)
 *   - dayIdx / setDayIdx     (currently-selected day index 0-6)
 *   - shiftsByDate           { "YYYY-MM-DD": [shift, …] }
 *   - hoursByDay             { "YYYY-MM-DD": number }
 *   - employees, locations   arrays
 *   - openEdit(shift)        open the shift-editor modal
 *   - openNew(dateISO, empId?) open a fresh shift for a given day (and optional employee)
 *   - onWeekShift(direction) -1 / +1 to switch to previous / next week
 *   - t()                    i18n helper
 */
export default function MobileRoster({
  days,
  dayIdx,
  setDayIdx,
  shiftsByDate,
  hoursByDay,
  employees,
  locations,
  openEdit,
  openNew,
  onWeekShift,
  onPublishWeek,
  onCopyPrevWeek,
  onCopyToNextWeek,
  onOpenCopyPicker,
  t,
}) {
  const [tab, setTab] = useState("shifts"); // "shifts" | "team"
  const [showMenu, setShowMenu] = useState(false);
  // Subscribes to midnight-tick so week-strip + "Vandaag" chip refresh live.
  const liveTodayISO = useTodayISO();

  const selectedDay = days[dayIdx];
  const dayISO = fmtDate(selectedDay);
  const dayShifts = useMemo(
    () => (shiftsByDate[dayISO] || []).slice().sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [shiftsByDate, dayISO]
  );
  const totalHours = hoursByDay[dayISO] || 0;
  const unassignedCount = dayShifts.filter((s) => !s.employee_id).length;
  const assignedCount = dayShifts.length - unassignedCount;
  const workingEmpIds = useMemo(
    () => new Set(dayShifts.map((s) => s.employee_id).filter(Boolean)),
    [dayShifts]
  );

  const empName = (id) => employees.find((e) => e._id === id)?.name || t("unassigned");
  const locName = (id) => locations.find((l) => l._id === id)?.name || "";
  const locColor = (id) => locations.find((l) => l._id === id)?.color || "hsl(var(--primary))";

  // Group shifts by employee for the "Team" tab. Include every employee — those
  // without a shift today show an "off duty" state, which is the whole point of
  // the tab (managers want to see WHO is missing).
  const teamRows = useMemo(() => {
    const map = {};
    for (const s of dayShifts) {
      if (!s.employee_id) continue;
      (map[s.employee_id] = map[s.employee_id] || []).push(s);
    }
    return [...employees]
      .sort((a, b) => {
        const aw = workingEmpIds.has(a._id) ? 0 : 1;
        const bw = workingEmpIds.has(b._id) ? 0 : 1;
        if (aw !== bw) return aw - bw;
        return a.name.localeCompare(b.name);
      })
      .map((e) => ({ employee: e, shifts: map[e._id] || [] }));
  }, [dayShifts, employees, workingEmpIds]);

  const unassignedShifts = dayShifts.filter((s) => !s.employee_id);

  const todayISO = liveTodayISO;
  const isToday = dayISO === todayISO;

  const dayLabel = selectedDay.toLocaleDateString("nl", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="pb-24" data-testid="mobile-roster">
      {/* ── Week strip (sticky under the page header) ────────────────────── */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-3 mb-3 bg-[hsl(var(--bg))]/85 backdrop-blur-xl border-b hairline">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onWeekShift(-1)}
            data-testid="mroster-week-prev"
            className="w-9 h-9 grid place-items-center rounded-full bg-[hsl(var(--surface))] border hairline active:scale-95 transition-transform shrink-0"
            aria-label="prev-week"
          >
            <ChevronLeft size={16} strokeWidth={2} />
          </button>

          {/* 7-day pill strip */}
          <div className="flex-1 grid grid-cols-7 gap-1">
            {days.map((d, idx) => {
              const iso = fmtDate(d);
              const shiftsCount = (shiftsByDate[iso] || []).length;
              const active = idx === dayIdx;
              const isTodayCell = iso === todayISO;
              return (
                <button
                  key={iso}
                  onClick={() => setDayIdx(idx)}
                  data-testid={`mroster-day-${iso}`}
                  className={`flex flex-col items-center justify-center gap-0.5 rounded-2xl py-2 transition-all ${
                    active
                      ? "bg-[hsl(var(--primary))] text-white shadow-[var(--shadow-sm)]"
                      : "bg-[hsl(var(--surface))] text-[hsl(var(--text-strong))] border hairline active:scale-95"
                  }`}
                >
                  <span className={`text-[9px] font-bold uppercase tracking-wider leading-none ${active ? "opacity-80" : "text-[hsl(var(--text-muted))]"}`}>
                    {WEEKDAYS_NL[(d.getDay() + 6) % 7]}
                  </span>
                  <span className={`font-display text-[16px] font-bold tabular-nums leading-tight ${
                    !active && isTodayCell ? "text-[hsl(var(--primary))]" : ""
                  }`}>
                    {d.getDate()}
                  </span>
                  {shiftsCount > 0 && (
                    <span className={`w-1 h-1 rounded-full ${active ? "bg-white/90" : "bg-[hsl(var(--primary))]"}`} />
                  )}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => onWeekShift(1)}
            data-testid="mroster-week-next"
            className="w-9 h-9 grid place-items-center rounded-full bg-[hsl(var(--surface))] border hairline active:scale-95 transition-transform shrink-0"
            aria-label="next-week"
          >
            <ChevronRight size={16} strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* ── Day hero ─────────────────────────────────────────────────────── */}
      <motion.div
        key={dayISO}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="mb-3"
      >
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--primary))]">
              {isToday ? t("today") : t("mroster_selected_day")}
            </div>
            <h2 className="font-display text-[26px] font-bold leading-tight tracking-tight text-[hsl(var(--text-strong))] mt-0.5 capitalize truncate">
              {dayLabel}
            </h2>
          </div>
          {/* Compact action menu — publish + copy options */}
          <div className="relative shrink-0">
            <button
              onClick={() => setShowMenu((v) => !v)}
              data-testid="mroster-menu-btn"
              className="w-10 h-10 grid place-items-center rounded-full bg-[hsl(var(--surface))] border hairline active:scale-95 transition-transform"
              aria-label="menu"
            >
              <MoreHorizontal size={18} strokeWidth={2} />
            </button>
            <AnimatePresence>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.96 }}
                    transition={{ duration: 0.16, ease: [0.4, 0, 0.2, 1] }}
                    className="absolute right-0 top-full mt-2 w-64 rounded-2xl bg-[hsl(var(--surface))] border hairline z-50 overflow-hidden"
                    style={{ boxShadow: "var(--shadow-lg)" }}
                    data-testid="mroster-menu"
                  >
                    {onPublishWeek && (
                      <button
                        onClick={() => { setShowMenu(false); onPublishWeek(); }}
                        data-testid="mroster-menu-publish"
                        className="w-full flex items-center gap-3 px-4 py-3 active:bg-[hsl(var(--muted))] transition-colors text-left"
                      >
                        <span className="w-8 h-8 grid place-items-center rounded-full bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] shrink-0">
                          <Send size={14} strokeWidth={2} />
                        </span>
                        <span className="text-[13.5px] font-semibold text-[hsl(var(--text-strong))]">
                          {t("publish_week")}
                        </span>
                      </button>
                    )}
                    {(onCopyPrevWeek || onCopyToNextWeek || onOpenCopyPicker) && (
                      <div className="h-px bg-[hsl(var(--border))]" />
                    )}
                    {onCopyPrevWeek && (
                      <button
                        onClick={() => { setShowMenu(false); onCopyPrevWeek(); }}
                        data-testid="mroster-menu-copy-prev"
                        className="w-full flex items-center gap-3 px-4 py-3 active:bg-[hsl(var(--muted))] transition-colors text-left"
                      >
                        <span className="w-8 h-8 grid place-items-center rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] shrink-0">
                          <ChevronLeft size={14} strokeWidth={2} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">
                            {t("copy_prev_week")}
                          </div>
                          <div className="text-[10.5px] text-[hsl(var(--text-muted))] truncate">
                            {t("copy_prev_week_hint")}
                          </div>
                        </div>
                      </button>
                    )}
                    {onCopyToNextWeek && (
                      <button
                        onClick={() => { setShowMenu(false); onCopyToNextWeek(); }}
                        data-testid="mroster-menu-copy-next"
                        className="w-full flex items-center gap-3 px-4 py-3 active:bg-[hsl(var(--muted))] transition-colors text-left"
                      >
                        <span className="w-8 h-8 grid place-items-center rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] shrink-0">
                          <ChevronRight size={14} strokeWidth={2} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">
                            {t("copy_to_next_week")}
                          </div>
                          <div className="text-[10.5px] text-[hsl(var(--text-muted))] truncate">
                            {t("copy_to_next_week_hint")}
                          </div>
                        </div>
                      </button>
                    )}
                    {onOpenCopyPicker && (
                      <button
                        onClick={() => { setShowMenu(false); onOpenCopyPicker(); }}
                        data-testid="mroster-menu-copy-pick"
                        className="w-full flex items-center gap-3 px-4 py-3 active:bg-[hsl(var(--muted))] transition-colors text-left"
                      >
                        <span className="w-8 h-8 grid place-items-center rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] shrink-0">
                          <Copy size={14} strokeWidth={2} />
                        </span>
                        <span className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">
                          {t("copy_pick_days")}
                        </span>
                      </button>
                    )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="rounded-2xl bg-[hsl(var(--surface))] border hairline p-3 shadow-[var(--shadow-xs)]">
            <div className="font-display text-[20px] font-bold tabular-nums leading-none text-[hsl(var(--text-strong))]">
              {dayShifts.length}
            </div>
            <div className="text-[10.5px] font-semibold text-[hsl(var(--text-muted))] mt-1">
              {t("mroster_tab_shifts")}
            </div>
          </div>
          <div className="rounded-2xl bg-[hsl(var(--surface))] border hairline p-3 shadow-[var(--shadow-xs)]">
            <div className="font-display text-[20px] font-bold tabular-nums leading-none text-[hsl(var(--primary))]">
              {fmtHours(totalHours)}
            </div>
            <div className="text-[10.5px] font-semibold text-[hsl(var(--text-muted))] mt-1">
              {t("total_hours")}
            </div>
          </div>
          <div className={`rounded-2xl border hairline p-3 shadow-[var(--shadow-xs)] ${
            unassignedCount > 0 ? "bg-[hsl(var(--danger-soft))]" : "bg-[hsl(var(--surface))]"
          }`}>
            <div className={`font-display text-[20px] font-bold tabular-nums leading-none ${
              unassignedCount > 0 ? "text-[hsl(var(--danger))]" : "text-[hsl(var(--text-strong))]"
            }`}>
              {unassignedCount}
            </div>
            <div className={`text-[10.5px] font-semibold mt-1 ${
              unassignedCount > 0 ? "text-[hsl(var(--danger))]" : "text-[hsl(var(--text-muted))]"
            }`}>
              {t("open_shifts")}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Tabs: Shifts vs Team ─────────────────────────────────────────── */}
      <div className="flex items-center gap-1 bg-[hsl(var(--muted))] rounded-full p-1 mb-3">
        <button
          onClick={() => setTab("shifts")}
          data-testid="mroster-tab-shifts"
          className={`flex-1 h-9 rounded-full text-[12.5px] font-semibold transition-all inline-flex items-center justify-center gap-1.5 ${
            tab === "shifts"
              ? "bg-[hsl(var(--surface))] text-[hsl(var(--text-strong))] shadow-[var(--shadow-xs)]"
              : "text-[hsl(var(--text-muted))]"
          }`}
        >
          <ListChecks size={13} strokeWidth={2.25} />
          {t("mroster_tab_shifts")}
        </button>
        <button
          onClick={() => setTab("team")}
          data-testid="mroster-tab-team"
          className={`flex-1 h-9 rounded-full text-[12.5px] font-semibold transition-all inline-flex items-center justify-center gap-1.5 ${
            tab === "team"
              ? "bg-[hsl(var(--surface))] text-[hsl(var(--text-strong))] shadow-[var(--shadow-xs)]"
              : "text-[hsl(var(--text-muted))]"
          }`}
        >
          <Users size={13} strokeWidth={2.25} />
          {t("mroster_tab_team")}
        </button>
      </div>

      {/* ── Content ──────────────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {tab === "shifts" ? (
          <motion.div
            key={`shifts-${dayISO}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
          >
            {/* Unassigned alert */}
            {unassignedShifts.length > 0 && (
              <div
                className="rounded-2xl bg-[hsl(var(--danger-soft))] border border-[hsl(var(--danger))]/20 p-3 mb-3 flex items-center gap-2.5"
                data-testid="mroster-unassigned-alert"
              >
                <span className="grid place-items-center w-8 h-8 rounded-full bg-white/70 text-[hsl(var(--danger))] shrink-0">
                  <AlertCircle size={16} strokeWidth={2} />
                </span>
                <div className="text-[12.5px] font-semibold text-[hsl(var(--danger))] leading-snug">
                  {unassignedShifts.length === 1
                    ? t("mroster_unassigned_single")
                    : t("mroster_unassigned_multi").replace("{n}", unassignedShifts.length)}
                </div>
              </div>
            )}

            {dayShifts.length === 0 ? (
              <div className="py-10 px-4 text-center" data-testid="mroster-empty">
                <div className="mb-2">
                  <PaperPlaneEmpty size={120} />
                </div>
                <div className="font-display text-[17px] font-bold text-[hsl(var(--text-strong))]">
                  {t("empty_roster_title")}
                </div>
                <div className="text-[13px] text-[hsl(var(--text-muted))] mt-1.5 max-w-[260px] mx-auto">
                  {t("empty_roster_hint")}
                </div>
                <button
                  onClick={() => openNew(dayISO)}
                  data-testid="mroster-empty-add"
                  className="btn btn-primary mt-4 mx-auto"
                >
                  <Plus size={14} strokeWidth={2} /> {t("add_shift")}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {dayShifts.map((s, idx) => (
                  <ShiftCard
                    key={s._id}
                    shift={s}
                    employeeName={s.employee_id ? empName(s.employee_id) : t("unassigned")}
                    locName={locName(s.location_id)}
                    locColor={locColor(s.location_id)}
                    onClick={() => openEdit(s)}
                    index={idx}
                  />
                ))}
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key={`team-${dayISO}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
          >
            {employees.length === 0 ? (
              <div className="card p-8 text-center">
                <div className="text-[13px] text-[hsl(var(--text-muted))]">{t("no_employees_yet")}</div>
              </div>
            ) : (
              <>
                {/* Summary line */}
                <div className="text-[11.5px] font-medium text-[hsl(var(--text-muted))] mb-2.5 px-1">
                  {t("mroster_team_summary")
                    .replace("{working}", workingEmpIds.size)
                    .replace("{total}", employees.length)}
                </div>
                <div className="space-y-2">
                  {teamRows.map((row, idx) => (
                    <EmployeeRow
                      key={row.employee._id}
                      employee={row.employee}
                      shifts={row.shifts.sort((a, b) => a.start_time.localeCompare(b.start_time))}
                      locColor={locColor}
                      locName={locName}
                      onEditShift={openEdit}
                      onAddShift={(iso, empId) => {
                        // openNew supports (date) — pass empId via optional second arg,
                        // the parent will decide whether to prefill.
                        openNew(iso, empId);
                      }}
                      dayISO={dayISO}
                      t={t}
                      index={idx}
                    />
                  ))}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Floating action button ───────────────────────────────────────── */}
      <button
        onClick={() => openNew(dayISO)}
        data-testid="mroster-fab-add"
        className="fixed right-4 z-30 h-14 w-14 rounded-full bg-[hsl(var(--primary))] text-white grid place-items-center shadow-[0_10px_30px_-8px_hsl(var(--primary)_/_0.55)] active:scale-95 transition-transform"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)" }}
        aria-label={t("add_shift")}
      >
        <Plus size={22} strokeWidth={2.25} />
      </button>
    </div>
  );
}
